import React, { useState } from 'react';
import { FlameIcon, MoonIcon } from 'lucide-react';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useDarkMode } from '../context/DarkModeContext';
const spiceLevels = [{
  name: 'Mild',
  level: 1,
  count: 125,
  description: 'Sweet kisses and cuddles. No on-page intimacy.',
  examples: ['Closed Door', 'Slow Burn', 'Sweet Romance']
}, {
  name: 'Warm',
  level: 2,
  count: 97,
  description: 'Flirting, some heat, and fade-to-black scenes.',
  examples: ['Open Door', 'Tension', 'Makeout Scenes']
}, {
  name: 'Spicy',
  level: 3,
  count: 83,
  description: 'On-page steamy scenes and descriptive romance.',
  examples: ['One Bed', 'High Heat', 'Dirty Talk']
}, {
  name: 'Extra Hot',
  level: 4,
  count: 59,
  description: 'Multiple explicit scenes with kink or taboo.',
  examples: ['BDSM', 'Why Choose', 'Praise/Degradation']
}];
const darknessLevels = [{
  name: 'Grey',
  level: 1,
  count: 112,
  description: 'Light dark romance with emotional tension and minor trauma.',
  examples: ['Enemies to Lovers', 'Morally Grey', 'Jealousy']
}, {
  name: 'Charcoal',
  level: 2,
  count: 87,
  description: 'Darker themes like obsession, danger, and trauma.',
  examples: ['Kidnapping', 'Anti-Hero', 'Toxic Ex']
}, {
  name: 'Black',
  level: 3,
  count: 61,
  description: 'On-page violence, control, and damaged protagonists.',
  examples: ['Possessive', 'Revenge', 'Dub-Con']
}, {
  name: 'Ink Black',
  level: 4,
  count: 39,
  description: 'Psychological manipulation and morally bankrupt characters.',
  examples: ['Non-Con', 'Abuse', 'Power Imbalance']
}, {
  name: 'Pitch Black',
  level: 5,
  count: 18,
  description: 'Extreme dark content with brutal, irredeemable actions.',
  examples: ['Torture', 'Captivity', 'Sadism']
}];
const SpiceLevels: React.FC = () => {
  const [mode, setMode] = useState<'spice' | 'darkness'>('spice');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    darkMode
  } = useDarkMode();
  const levels = mode === 'spice' ? spiceLevels : darknessLevels;
  const TitleIcon = mode === 'spice' ? FlameIcon : MoonIcon;
  return <div className={dk.page} data-id="element-14534" data-mp="2e53u3ww2klyb">
      <ContentPageHeader title="Spice Levels" icon={getContentPageIcon('spicelevels')} searchPlaceholder="Search spice levels..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={showFilters} setShowFilters={setShowFilters} data-id="element-14535" data-mp="2e4calkfqyeoe" />
      {showFilters && <div className="py-4 border-t mt-4 max-w-7xl mx-auto px-4" data-id="element-14536" data-mp="2wwmyo85qs82g">
          {/* Filter controls */}
        </div>}
      <div className="mt-8 max-w-7xl mx-auto px-4" data-id="element-14537" data-mp="l9jpdmz66pnb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-14538" data-mp="l76ygkbfp4y4">
          {/* Toggle Buttons */}
          <div className="text-center mb-8 sm:mb-12" data-id="element-14539" data-mp="h9ssr2mypfav">
            <div className={`inline-flex space-x-2 border p-1 rounded-full ${dk.card}`} data-id="element-14540" data-mp="c9o9j4uo66gy">
              <button onClick={() => setMode('spice')} className={`px-4 py-1.5 rounded-full text-sm font-medium ${mode === 'spice' ? `${rt.bgDark} text-white` : `${dk.textSecondary} hover:${dk.surfaceMuted}`}`} data-id="element-14541" data-mp="7b38523olgqx">
                🔥 Spice
              </button>
              <button onClick={() => setMode('darkness')} className={`px-4 py-1.5 rounded-full text-sm font-medium ${mode === 'darkness' ? `${rt.bgDark} text-white` : `${dk.textSecondary} hover:${dk.surfaceMuted}`}`} data-id="element-14542" data-mp="v5wee7jrsfbq">
                🌑 Darkness
              </button>
            </div>
            {/* Heading */}
            <h1 className={`text-2xl sm:text-3xl font-bold mt-6 mb-3 sm:mb-4 flex justify-center items-center gap-2 ${dk.textHeading}`} data-id="element-14543" data-mp="13esj808mfyvu">
              <TitleIcon className="h-6 w-6" data-id="element-14544" data-mp="17eaue4an4vlh" />
              {mode === 'spice' ? 'Spice Levels' : 'Dark Romance Levels'}
            </h1>
            <p className={`text-base sm:text-lg max-w-2xl mx-auto ${dk.textSecondary}`} data-id="element-14545" data-mp="yf5vuxz2x29b">
              {mode === 'spice' ? 'Find books that match your preferred level of romantic content.' : 'Explore dark romance by intensity, from morally grey to pitch black.'}
            </p>
          </div>
          {/* Level Cards */}
          <div className="space-y-4 sm:space-y-6" data-id="element-14546" data-mp="1hejptfwt6czt">
            {levels.map(level => <div key={level.name} className={`rounded-lg shadow-md p-4 sm:p-6 ${dk.card}`} data-id="element-14547" data-mp="254nvdstk4mtg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4" data-id="element-14548" data-mp="29vw40mn2c45k">
                  <h2 className={`text-lg sm:text-xl font-semibold flex items-center ${dk.textHeading}`} data-id="element-14549" data-mp="3ujoqbwjtqmar">
                    <div className="flex space-x-1 mr-3" data-id="element-14550" data-mp="78yvqnv05t8p">
                      {[...Array(level.level)].map((_, i) => {
                    const Icon = mode === 'spice' ? FlameIcon : MoonIcon;
                    return <Icon key={i} className={`h-4 w-4 sm:h-5 sm:w-5 ${mode === 'spice' ? 'text-red-500' : dk.textPrimary}`} fill="currentColor" data-id="element-14551" data-mp="3bn741unqvelw" data-mpi={i} />;
                  })}
                    </div>
                    {level.name}
                  </h2>
                  <span className={`inline-flex items-center px-3 py-1 ${rt.badgeRadius} text-sm font-medium ${dk.badgeNeutral}`} data-id="element-14552" data-mp="zvtd3bwiwchy">
                    {level.count} books
                  </span>
                </div>
                <p className={`text-sm sm:text-base mb-3 sm:mb-4 ${dk.textSecondary}`} data-id="element-14553" data-mp="3toufa333vnjd">
                  {level.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-14554" data-mp="1jh3zhpiv4da">
                  {level.examples.map((example, index) => <span key={index} className={`inline-flex items-center px-2 sm:px-3 py-1 ${rt.badgeRadius} text-xs sm:text-sm ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`} data-id="element-14555" data-mp="35zrz6zo4e2qz" data-mpi={index}>
                      {example}
                    </span>)}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
};
export default SpiceLevels;